/** Public exercise data only. Expected answers belong in studentCorrection. */
export type ProgressSpecies = { id: string; label: string; coefficient: number; role: 'reactant'|'product'; initial: number };
export type ProgressModel = { species: ProgressSpecies[]; unit: string };
export type ProgressTableConfig = {
  species: Array<Omit<ProgressSpecies,'initial'|'coefficient'> & {initial?:number;coefficient?:number}>;
  unit:string;
  equation: string;
  equationSourceBlockId?:string;
  /** Keys are initial|current|final followed by .x or the species id. */
  fixedCells: Record<string,string>;
};
export type ResponseFields = { fields: Array<{id:string;label:string;input:'numeric'|'formula'|'text';unit?:string}>; equation?: boolean };
export function progressLimit(model: ProgressModel) {
  return Math.min(...model.species.filter(s=>s.role==='reactant').map(s=>s.initial/s.coefficient));
}
export function progressAmounts(model: ProgressModel,x:number) {
  if(!Number.isFinite(x)||x<0||x>progressLimit(model)+1e-12)throw new Error('Avancement hors du domaine physique.');
  return Object.fromEntries(model.species.map(s=>[s.id,Math.max(0,s.initial+(s.role==='reactant'?-1:1)*s.coefficient*x)]));
}
export function readStructuredAnswer(value:string):Record<string,string> {
  try {const parsed=JSON.parse(value);return parsed&&typeof parsed==='object'&&!Array.isArray(parsed)?Object.fromEntries(Object.entries(parsed).filter((entry):entry is [string,string]=>typeof entry[1]==='string')):{};}catch{return {};}
}
export function progressModelErrors(model:ProgressModel):string[] {
  if(!model||!Array.isArray(model.species)||model.species.length<2||model.species.length>12)return ['Espèces du modèle invalides.'];
  if(model.species.some(s=>!s||!s.id||!s.label||!['reactant','product'].includes(s.role)||!Number.isFinite(s.initial)||s.initial<0||!Number.isInteger(s.coefficient)||s.coefficient<=0)||new Set(model.species.map(s=>s.id)).size!==model.species.length||!model.species.some(s=>s.role==='reactant')||!model.species.some(s=>s.role==='product')||typeof model.unit!=='string')return ['Quantités, coefficients ou identifiants invalides.'];
  return [];
}


function progressNumber(value:number):string {
 if(!Number.isFinite(value))return '';
 if(Math.abs(value)<1e-15)return '0';
 const absolute=Math.abs(value);
 if(absolute>=0.01&&absolute<10000){
  const rounded=Math.round(value*1e12)/1e12;
  return String(rounded).replace('.',',');
 }
 const [mantissa,rawExponent]=value.toExponential(1).split('e');
 const exponent=Number(rawExponent);
 return `${mantissa.replace('.',',')}×10^${exponent}`;
}

export function progressCorrectionAnswer(config:ProgressTableConfig):string|null {
 const species=config.species;
 if(!species.length||species.some(s=>!Number.isFinite(s.initial)||!Number.isInteger(s.coefficient)||Number(s.coefficient)<=0))return null;
 const complete=species.map(s=>({...s,initial:Number(s.initial),coefficient:Number(s.coefficient)}));
 const reactants=complete.filter(s=>s.role==='reactant');
 if(!reactants.length)return null;
 const answers:Record<string,string>={
  'initial.x':'0',
  'current.x':'x',
  'final.x':'xₘₐₓ',
 };
 for(const s of complete){
  const currentTerm=s.coefficient===1?'x':`${s.coefficient}x`;
  const finalTerm=s.coefficient===1?'xₘₐₓ':`${s.coefficient}xₘₐₓ`;
  answers[`initial.${s.id}`]=progressNumber(s.initial);
  if(s.role==='reactant'){
   answers[`current.${s.id}`]=`${progressNumber(s.initial)} − ${currentTerm}`;
   answers[`final.${s.id}`]=`${progressNumber(s.initial)} − ${finalTerm}`;
  }else{
   answers[`current.${s.id}`]=s.initial===0?currentTerm:`${progressNumber(s.initial)} + ${currentTerm}`;
   answers[`final.${s.id}`]=s.initial===0?finalTerm:`${progressNumber(s.initial)} + ${finalTerm}`;
  }
 }
 return JSON.stringify(answers);
}
